import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserService } from '../user/user.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { Customer, CustomerInterface } from 'src/app/model/Customer';
import { AuthService } from '../auth/auth.service';
import { User } from 'src/app/model/User';
import { CreditPlan } from 'src/app/model/CreditPlan';
import {
  SUPPLIER_PLANS_ENDPOINT,
  CREDIT_SUMMARY_ENDPOINT,
  COMPANY_BORROWERS_ENDPOINT,
} from 'src/app/constants/api-routes';

/** Response interface for a credit summary */
export interface CreditSummaryResponse {
  creditLimit: number;
  creditInUse: number;
  authorizedHolds: number;
  remainingCredit: number;
}

/**
 * CustomerService is used to hold/move customer data from the backend to the application
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  /** A list of customers the user has access to */
  private _customers?: Customer[];
  private _customersSubject = new Subject<Customer[]>();
  public customers$ = this._customersSubject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>('0:00');
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the customer service, used to initialize services
   *
   * @param http
   * @param authService
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
    private _authService: AuthService,
  ) {
    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._customers = undefined;
        this._customersSubject.next([]);
      }
    });

    // Continuously query the backend to receive new applications
    this._startTimer(0);
    this.timer.subscribe((x: number) => {
      this._timers.splice(this._timers.indexOf(x));
      this._startTimer(x + 1);
      this._fetch();
    });
  }

  /**
   * Get a list of customers
   *
   * @return A reference to customers the user has access to
   */
  public get(): Customer[] | undefined {
    return this._customers;
  }

  /**
   * Get the credit plans available for a customer and their incoming transaction
   *
   * @param supplierId The ID of the supplier (customer) to get credit plans for
   * @returns An observable of the credit plans for the specified supplier
   */
  public getCreditPlans$(supplierId: string): Observable<CreditPlan[]> {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return of([]);

    // Validate supplier id
    if (!supplierId) return of([]);

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) return of([]);

    // Perform http request
    const url = SUPPLIER_PLANS_ENDPOINT(companyId, supplierId);
    return this._http.get<CreditPlan[]>(url).pipe(
      map((plans) =>
        plans.map((p) => {
          p.mdr = p.mdr / 100;
          return p;
        }),
      ),
    );
  }

  /**
   * Get a customer's KPI details
   *
   * @param companyIdToCheck The ID of the company to get details for
   * @return An observable of the customer's KPI details
   */
  public getCustomerCreditSummary$(
    companyIdToCheck: number,
  ): Observable<CreditSummaryResponse | null> {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return of(null);

    // Validate company id
    if (!companyIdToCheck) return of(null);

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) return of(null);

    // Call backend endpoint
    const url = CREDIT_SUMMARY_ENDPOINT(companyId, companyIdToCheck);
    return this._http.get<CreditSummaryResponse>(url);
  }

  /**
   * Set a list of customers
   *
   * @param customers The list of customers to set
   */
  private _set(customers: Customer[]): void {
    this._customers = customers.map((x) => x.copyOf());
    this._customersSubject.next(customers.map((x) => x.copyOf()));
  }

  /**
   * Get customers from the backend. You should subscribe to the 'customers$'
   * field to receive updates
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user's active company
    const company = this._userService.company;
    if (company == undefined) return;

    // Query the backend for customers
    this._http
      .get<CustomerInterface[]>(COMPANY_BORROWERS_ENDPOINT(company.companyId))
      .subscribe({
        next: (interfaces: CustomerInterface[]): void => {
          if (interfaces == undefined) {
            console.error(
              `[ERR]: cannot cast backend output of customers into list`,
            );
          }

          // Convert the interfaces to class values
          let customers: Customer[] = [];
          for (let intf of interfaces) {
            customers.push(new Customer(intf));
          }

          // Propagate changes if different
          this._set(customers);
          console.log(`[INFO]: successfully queried customers`);
        },
      });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await (function (delay: number) {
        return new Promise((r) => {
          setTimeout(r, delay);
        });
      })(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(
        `${minutes}:${('' + seconds).length === 1 ? '0' : ''}${seconds}`,
      );
    }
  }
}
